import * as React from 'react';
import {
  ReferenceBadge,
  Text,
  TextTag,
} from '@volkswagen-onehub/components-core';
import {
  useVwFeatureService,
  VwFeatureServiceIds,
} from '@volkswagen-onehub/vw-e-mobility-feature-app-commons';
import {
  DisclaimerKind,
  DisclaimerReferenceProps,
} from '@volkswagen-onehub/whitelabel-e-mobility-commons';
import {LayerHandleV2} from '@volkswagen-onehub/layer-manager';
import {DisclaimerLayerContent} from './disclaimer-layer-content';
import {CloseButton} from './close-button';
import {BrandThemeProvider} from '../theme';

export function DisclaimerReference({
  kind,
  reference,
  text,
  disclaimerTextId,
  ariaLabel,
  closeButtonAriaLabel,
}: DisclaimerReferenceProps): JSX.Element {
  const layerManager = useVwFeatureService(VwFeatureServiceIds.layerManager);
  const layerHandleRef = React.useRef<LayerHandleV2<undefined> | undefined>(
    undefined
  );

  const handleClick = React.useCallback(() => {
    let disclaimerContainer: HTMLElement | null;

    switch (kind) {
      case DisclaimerKind.AppRendered:
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- Existence is guaranteed by white-label API
        disclaimerContainer = document.getElementById(disclaimerTextId!);
        if (!disclaimerContainer) {
          return;
        }
        disclaimerContainer.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });

        // Sorry Lasse & Felix
        disclaimerContainer.querySelector<HTMLElement>('[tabindex]')?.focus();

        break;
      case DisclaimerKind.ExternallyRendered:
        if (layerHandleRef.current && !layerHandleRef.current.isClosed()) {
          layerHandleRef.current.close();
          layerHandleRef.current = undefined;
        } else {
          layerHandleRef.current = layerManager.openDisclaimerLayer(
            (_state, close) => (
              <DisclaimerLayerContent
                closeButton={
                  <CloseButton
                    onClick={close}
                    ariaLabel={closeButtonAriaLabel}
                  />
                }
                text={text}
                reference={`${reference}.`}
              />
            ),
            undefined,
            {userCloseable: true}
          );
        }
        break;
    }
  }, [
    closeButtonAriaLabel,
    disclaimerTextId,
    kind,
    layerManager,
    reference,
    text,
  ]);

  // todo: remove ThemeProvider and use inherited "inverted" theme when NGWD6-32551 is done
  return (
    <Text tag={TextTag.sup} sup staticSize>
      &nbsp;
      <BrandThemeProvider theme="main">
        <ReferenceBadge
          aria-label={ariaLabel}
          variant="default"
          onClick={handleClick}
        >
          {reference}
        </ReferenceBadge>
      </BrandThemeProvider>
    </Text>
  );
}
